


import React, { useState, useEffect, useCallback } from "react";
import useStyles from "./style";
import {
  Button, Grid, Typography, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, TablePagination,
} from "@mui/material";
import { EditOutlined, DeleteOutline, Search, PersonAddAlt1, ChevronLeft, ChevronRight } from "@mui/icons-material";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";

import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";

// Modals
import CustomercareCreateModal from "Pages/Modals/Customercare/Create";
import CustomercareDeleteDialog from "Pages/Modals/Customercare/Delete";

// Service
import customercareApi from "Services/Customercare";


const CustomerCare = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  // Primary list state - remove dummy data
  const [rows, setRows] = useState([]);

  const [searchText, setSearchText] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleSearchChange = (e) => { setSearchText(e.target.value); setPage(0); };
  const handleChangePage = (event, newPage) => { setPage(newPage); };
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

  // Fetch data from API

const getCustomerCareList = useCallback(() => {
  dispatch(showLoader("Loading..."));
  customercareApi.getCustomercareList({ search: searchText })
   .then(res => {
      const list = res?.data?.data?.data || [];   //
      console.log("FULL RESPONSE:", list);
      console.log("STEP 1 - res:", res);
    console.log("STEP 2 - res.data:", res?.data);
    console.log("STEP 3 - res.data.data:", res?.data?.data);
    console.log("STEP 4 - res.data.data.data:", res?.data?.data?.data);

      const mapped = list.map((item, idx) => ({
        _id: item._id,
        id: `#CC-${1000 + idx + 1}`,
        fullName: item.user_name,
        email: item.email,
        mobile: `${item.countryCode || ''} ${item.mobileNumber || ''}`,
        status: item.isActive === 1 ? "Active" : "Inactive",
        country: item.country,
        countryCode: item.countryCode,
        mobileNumber: item.mobileNumber,
      }));
      setRows(mapped);
      dispatch(hideLoader());
    })
   .catch(err => {
      console.error("API Error:", err);   //
      dispatch(hideLoader());
      dispatch(showSnackbar({ message: "Failed to fetch", variant: "error" }));
    });
}, [dispatch, searchText]);

  useEffect(() => {
    getCustomerCareList();
    
  }, [getCustomerCareList]);

  // Actions
  const handleOpenAddModal = () => { setIsEditMode(false); setSelectedRow(null); setCreateModalOpen(true); };
  const handleOpenEditModal = (row) => { setIsEditMode(true); setSelectedRow(row); setCreateModalOpen(true); };
  const handleOpenDeleteDialog = (row) => { setSelectedRow(row); setDeleteDialogOpen(true); };

  // This will be called by modal after success
  const handleModalSuccess = () => {
    setCreateModalOpen(false); // close popup
    getCustomerCareList(); // refresh table
  }

 
 
  const handleDeleteSubmit = () => {
    if(!selectedRow?._id) return;
    
    dispatch(showLoader("Deleting..."));
    customercareApi.deleteCustomercare(selectedRow._id) // API call
     .then(res => {
        dispatch(hideLoader());
        if(res?.data?.status){ // your backend uses status: true
          dispatch(showSnackbar({ 
            message: res.data.message || "Deleted successfully", 
            variant: "success",
            anchorOrigin: { vertical: "top", horizontal: "right" }
          }));
          setDeleteDialogOpen(false);
          setSelectedRow(null);
          getCustomerCareList(); // <-- IMPORTANT: Refresh table from backend
        } else {
          dispatch(showSnackbar({ message: res.data.message || "Delete failed", variant: "error" }));
        }
      })
     .catch((err) => { 
        dispatch(hideLoader()); 
        dispatch(showSnackbar({ message: err?.response?.data?.message || "Delete failed", variant: "error" })); 
      });
  };
 
 
  const filteredRows = rows.filter((row) => {
    const search = searchText.toLowerCase();
    return (
      row.id?.toLowerCase().includes(search) ||
      row.fullName?.toLowerCase().includes(search) ||
      row.email?.toLowerCase().includes(search) ||
      row.mobileNumber?.toLowerCase().includes(search)
    );
  });

  //... keep all your export functions same...

 // Export functions
  const handleCopy = () => {
    const columnWidths = [12, 20, 30, 20, 12];
    const padText = (text, width) => (text ?? "").toString().padEnd(width, " ");
    const header = [
      padText("ID", columnWidths[0]),
      padText("FULL NAME", columnWidths[1]),
      padText("EMAIL ADDRESS", columnWidths[2]),
      padText("MOBILE", columnWidths[3]),
      padText("STATUS", columnWidths[4]),
    ].join("");

    const dataText = filteredRows
      .map((row) =>
        [
          padText(row.id, columnWidths[0]),
          padText(row.fullName, columnWidths[1]),
          padText(row.email, columnWidths[2]),
          padText(row.mobile, columnWidths[3]),
          padText(row.status, columnWidths[4]),
        ].join("")
      )
      .join("\n");

    const textToCopy = [header, dataText].join("\n");
    navigator.clipboard.writeText(textToCopy).then(() => {
      dispatch(
        showSnackbar({
          message: "Copied table to clipboard!",
          variant: "success",
          autoHideDuration: 2000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        })
      );
    });
  };

  const handleExportExcel = () => {
    const data = filteredRows.map((r) => ({
      ID: r.id,
      "FULL NAME": r.fullName,
      "EMAIL ADDRESS": r.email,
      MOBILE: r.mobile,
      STATUS: r.status,
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customer Care");
    XLSX.writeFile(workbook, "CustomerCare_List.xlsx");
  };

  const handleExportCSV = () => {
    const data = filteredRows.map((r) => ({
      ID: r.id,
      "FULL NAME": r.fullName,
      "EMAIL ADDRESS": r.email,
      MOBILE: r.mobile,
      STATUS: r.status,
    }));
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "CustomerCare_List.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const tableData = filteredRows.map((r) => [
      r.id,
      r.fullName,
      r.email,
      r.mobile,
      r.status,
    ]);
    doc.autoTable({
      head: [["ID", "FULL NAME", "EMAIL ADDRESS", "MOBILE", "STATUS"]],
      body: tableData,
    });
    doc.save("CustomerCare_List.pdf");
  };

  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);

  const renderPagination = () => {
    let pages = [];

    if (totalPages <= 5) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page < 3) {
        pages = [0, 1, 2, "...", totalPages - 1];
      } else if (page > totalPages - 4) {
        pages = [
          0,
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
        ];
      } else {
        pages = [
          0,
          "...",
          page - 1,
          page,
          page + 1,
          "...",
          totalPages - 1,
        ];
      }
    }

    return pages.map((item, index) => {
      if (item === "...") {
        return (
          <span key={index} className={classes.pageDots}>
            ...
          </span>
        );
      }

      return (
        <Button
          key={item}
          className={
            page === item
              ? classes.pageButtonActive
              : classes.pageButton
          }
          onClick={() => setPage(item)}
        >
          {item + 1}
        </Button>
      );
    });
  };


  return (
    <Box className={classes.root}>
      {/* Header Container */}
      <Box className={classes.headerContainer}>
        <Typography className={classes.pageTitle}>Customer Care</Typography>
        <Typography className={classes.breadcrumb}>Admin / <span>Customer Care</span></Typography>
      </Box>

      {/* Actions and Search Bar Row */}
      <Box className={classes.actionRow}>
        <Box className={classes.searchWrapper}>
          <Search className={classes.searchIcon} />
          <input type="text" placeholder="Search" value={searchText} onChange={handleSearchChange} className={classes.searchInput} />
        </Box>
        <Box style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <Box className={classes.exportGroup}>
            <Button className={classes.exportButton} onClick={handleCopy}>Copy</Button>
            <Button className={classes.exportButton} onClick={handleExportExcel}>Excel</Button>
            <Button className={classes.exportButton} onClick={handleExportCSV}>CSV</Button>
            <Button className={classes.exportButton} onClick={handleExportPDF}>PDF</Button>
          </Box>
          <Button variant="contained" className={classes.addCustomerButton} onClick={handleOpenAddModal} startIcon={<PersonAddAlt1 />}>
            Add Customer Care
          </Button>
        </Box>
      </Box>

      {/* Data Table */}
      <TableContainer component={Paper} elevation={0} className={classes.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeaderRow}>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 1.2 }}>ID</TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 1.8 }}>Full Name</TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 2.5 }}>Email Address</TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 2 }}>Mobile</TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 1.5 }}>Status</TableCell>
              <TableCell className={classes.tableHeaderCell} style={{ flex: 2, textAlign: "right" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
              <TableRow key={row._id} className={classes.tableRow}>
                <TableCell className={classes.tableCell} style={{ flex: 1.2 }}>{row.id}</TableCell>
                <TableCell className={`${classes.tableCell} ${classes.boldText}`} style={{ flex: 1.8 }}>{row.fullName}</TableCell>
                <TableCell className={classes.tableCell} style={{ flex: 2.5 }}>{row.email}</TableCell>
                <TableCell className={classes.tableCell} style={{ flex: 2 }}>{`${row.countryCode} ${row.mobileNumber}`}</TableCell>
                <TableCell className={classes.tableCell} style={{ flex: 1.5 }}>
                  <span className={row.status === "Active"? classes.statusBadgeActive : classes.statusBadgeInactive}>{row.status}</span>
                </TableCell>
                <TableCell className={classes.tableCell} style={{ flex: 2, textAlign: "right" }}>
                  <Box component="span" className={classes.actionButton} onClick={() => handleOpenEditModal(row)}><EditOutlined fontSize="small" /> Edit</Box>
                  <Box component="span" className={classes.actionDeleteButton} onClick={() => handleOpenDeleteDialog(row)}><DeleteOutline fontSize="small" /> Delete</Box>
                </TableCell>
              </TableRow>
            ))}
            {filteredRows.length === 0 && (
              <TableRow><TableCell colSpan={6} style={{ textAlign: "center", padding: "32px" }}><Typography variant="body2" color="textSecondary">No records found</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <Box className={classes.paginationContainer}>
          <Typography className={classes.paginationText}>Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, filteredRows.length)} of {filteredRows.length} results</Typography>
          <Box className={classes.paginationControls}>
            <ChevronLeft className={`${classes.pageArrow} ${page === 0? "disabled" : ""}`} onClick={() => page > 0 && setPage(page - 1)} />
            {renderPagination()}
            <ChevronRight className={`${classes.pageArrow} ${page === totalPages - 1? "disabled" : ""}`} onClick={() => page < totalPages - 1 && setPage(page + 1)} />
          </Box>
        </Box>
      </TableContainer>

      {/* Create / Edit Dialog - FIX: pass onSuccess instead of onSubmit */}
      <CustomercareCreateModal
        openDialog={createModalOpen}
        handleClose={() => setCreateModalOpen(false)}
        onSuccess={handleModalSuccess} // <-- changed
        isEditMode={isEditMode}
        initialValues={selectedRow}
      />

      {/* Delete Dialog */}
      <CustomercareDeleteDialog
        openDialog={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        onSubmit={handleDeleteSubmit}
        row={selectedRow}
      />
    </Box>
  );
};

export default CustomerCare;
